import type { FileRecord } from "@cohesive/api-client";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { BlockDisplay } from "../../../lib/blockDisplay";
import { loadBlockFonts } from "../../../lib/blockFonts";
import {
  createBlockClickMessage,
  createBlockConfigMessage,
  createBlockFontsMessage,
  createBlockHoverLeaveMessage,
  createBlockHoverMessage,
  parseBlockMessage,
  type BlockEntryPoint,
  type BlockHoverKind,
  type BlockToParentMessage,
} from "../../../lib/htmlBlockRuntime";
import {
  blockPrepareOptionsSalt,
  buildBlockPrepareOptions,
  prepareSrcDocHtml,
} from "../../../lib/htmlIframeSrcDoc";
import { cn } from "../../../lib/utils";
import { getFileNameFromPath } from "../FileRecordCard/FileRecordCard";
import { HtmlBlockShield } from "./HtmlBlockShield";
import { HtmlCardPlaceholder } from "./HtmlCardPlaceholder";
import { useHtmlCardSrcDoc } from "./useHtmlCardSrcDoc";
import {
  useIframeMountAdmission,
  type IframeMountPolicy,
} from "./useIframeMountAdmission";

export type HtmlBlockCardCanvasProps = Pick<
  FileRecord,
  "url" | "path" | "updated_at"
> & {
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  /** The user is INSIDE the block (interact or edit mode) — the chrome reads
   *  blue to distinguish focus-for-interaction from a mere selection. */
  focused?: boolean;
  /** True while the parent NodeResizer is active — the shield remounts so
   *  the resize cursor isn't captured by the iframe. */
  resizing?: boolean;
  /** True when the iframe should receive pointer events (block entered by
   *  double-click, minus gesture exceptions — EntityNode owns that
   *  expression). */
  isInteractive?: boolean;
  /** The block's display model (chromeless etc.), resolved by the caller
   *  through blockDisplayFromNodeData. */
  display: BlockDisplay;
  /** `"progressive"` paces the iframe mount through the shared admission
   *  scheduler (public share view); default `"eager"` mounts as soon as the
   *  document is ready — the editor's unchanged behavior. */
  mountPolicy?: IframeMountPolicy;
  /** Server-rendered card preview shown until the live iframe is up —
   *  the share view's placeholder while a progressive mount waits its turn.
   *  Falls back to the loading skeleton when absent. */
  previewImageUrl?: string | null;
  /** Active app theme; drives the injected color-scheme so transparent blocks
   *  composite correctly (mismatched schemes paint an opaque backstop).
   *  Defaults to light. */
  theme?: "light" | "dark";
  /** Optional async rewrite applied to the fetched HTML before rendering —
   *  used to swap embedded public file URLs for presigned ones. Memoize it;
   *  identity changes do not retrigger a fetch. */
  transformHtml?: (html: string) => Promise<string> | string;
  /** Edit session active: the block runtime enables contenteditable islands
   *  and the iframe receives pointer events. */
  islandsEditable?: boolean;
  /** While true, refetched content is stashed instead of committed so the
   *  iframe never remounts mid edit-session. */
  contentFrozen?: boolean;
  /** Capability-aware affordance. The label is painted by node chrome
   *  outside the block box; here it only marks the shield as a click target.
   *  Null/undefined means the block is live or loading. */
  hintLabel?: string | null;
  /** Hovering the card should faintly outline editable islands (host-driven
   *  through the runtime — the shield keeps :hover out of the iframe). */
  islandHint?: boolean;
  /** Caret target for an edit session entered by double-click, in iframe
   *  content coordinates. Forwarded with the runtime config. */
  editFocusPoint?: BlockEntryPoint | null;
  /** The runtime announced itself for the current document; `islands` is the
   *  live count of editable islands inside the opaque frame. */
  onRuntimeReady?: (info: { islands: number }) => void;
  /** Runtime messages (island edits, flush/exit requests, edit requests) —
   *  link and click-result messages are handled internally. */
  onBlockMessage?: (msg: BlockToParentMessage) => void;
  onDoubleClick?: (point?: BlockEntryPoint) => void;
  /**
   * Forward qualifying single clicks into a resting block as activations
   * while the shield keeps the real pointer. The caller owns the canvas-side
   * guards (multi-select, hand tool, dragging); the card only enforces the
   * gesture-shape ones — first click, no selection modifiers, no movement
   * since pointerdown — because only it sees the raw event.
   */
  clickThrough?: boolean;
  /**
   * A forwarded click landed on a control. The doorway of the interaction
   * model: the caller responds by entering inline interactive mode, so the
   * first activation is forwarded and everything after is native.
   */
  onInteractiveClickThrough?: () => void;
  /**
   * The current document has been revealed — fired from the same keyed
   * reveal that swaps the placeholder out, so it inherits the load-or-ready
   * race protection (once per document, whichever signal lands first, never
   * for a replaced document). The lightbox animates its stage in on this
   * rather than around an empty card.
   */
  onDisplayReady?: () => void;
};

/**
 * Canvas HTML block: the document rendered 1:1 at exactly the node's size
 * with the app color scheme, Cohesive tokens, fonts, and the cohesive-block
 * runtime injected. One srcDoc serves display, interactive, and edit modes —
 * the parent flips modes via config messages, never remounts, so in-block JS
 * state survives them (see useHtmlCardSrcDoc for the remount discipline).
 *
 * Pointer ownership: unless the block is live (inline interactive or an edit
 * session), an HtmlBlockShield sits above the iframe so every canvas gesture
 * — wheel pan, drag pan, node drag, marquee — works exactly as over any other
 * node. Single clicks at rest are forwarded through the shield into the
 * frame (click-through); a click that lands on a control is the doorway
 * into inline interactive mode.
 */
export function HtmlBlockCardCanvas({
  url,
  path,
  updated_at,
  className,
  style,
  selected,
  focused = false,
  resizing = false,
  isInteractive = false,
  display,
  mountPolicy = "eager",
  previewImageUrl = null,
  theme = "light",
  transformHtml,
  islandsEditable = false,
  contentFrozen = false,
  hintLabel = null,
  islandHint = false,
  editFocusPoint = null,
  onRuntimeReady,
  onBlockMessage,
  onDoubleClick,
  clickThrough = false,
  onInteractiveClickThrough,
  onDisplayReady,
}: HtmlBlockCardCanvasProps) {
  const iframeActive = (isInteractive || islandsEditable) && !resizing;

  // Chromeless = no card chrome AND a transparent ground: the HTML
  // composites straight onto the canvas.
  const frameless = display.chromeless;
  const transparent = display.chromeless;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  // The document currently revealed, by its mount key. Deriving visibility
  // from the key (rather than a boolean reset in an effect) means a reveal
  // can never be undone by a later effect and a signal from a replaced
  // document can never reveal the new one — whatever order `load`, the
  // runtime's `ready` and React's effects arrive in. WebKit fires `load`
  // for a srcdoc frame within a few ms of insertion, before passive
  // effects have run; the boolean version lost that reveal.
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  // The theme is read through a ref: the document is prepared for the theme
  // of the moment it mounts, and later flips reach the running runtime as
  // config (applied in place) — so neither `prepare`'s identity nor the
  // options salt may change with it, or every block would refetch and
  // remount on a theme toggle.
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const prepare = useCallback(
    (transformed: string) =>
      prepareSrcDocHtml(
        transformed,
        buildBlockPrepareOptions({
          theme: themeRef.current,
          transparent,
        }),
      ),
    [transparent],
  );

  const { displayDoc } = useHtmlCardSrcDoc({
    url,
    updated_at,
    transformHtml,
    optionsSalt: blockPrepareOptionsSalt(transparent),
    prepare,
    contentFrozen,
  });

  // `ready` keeps this card out of the scheduler until its document can
  // actually mount, so an admission slot always paces a real iframe.
  const { admitted, onIframeLoad } = useIframeMountAdmission({
    policy: mountPolicy,
    hostRef,
    ready: displayDoc !== null,
  });

  // Reveal on whichever arrives first: the iframe's `load` event or the
  // runtime's `ready` message. `load` waits for every subresource the
  // document references, so a stalled third-party fetch (a fonts host under
  // Safari's Private Relay or tracking prevention) would otherwise keep a
  // fully parsed, painting document hidden behind the placeholder. One
  // reveal per document — the second signal is a no-op, so the admission
  // scheduler's slot is returned exactly once.
  const mountKey = displayDoc?.mountKey ?? null;
  const mountKeyRef = useRef(mountKey);
  useLayoutEffect(() => {
    mountKeyRef.current = mountKey;
  }, [mountKey]);
  const slotReturnedForRef = useRef<string | null>(null);
  const onDisplayReadyRef = useRef(onDisplayReady);
  onDisplayReadyRef.current = onDisplayReady;
  const reveal = useCallback(() => {
    const key = mountKeyRef.current;
    if (!key) return;
    setRevealedKey((prev) => (prev === key ? prev : key));
    if (slotReturnedForRef.current !== key) {
      slotReturnedForRef.current = key;
      onIframeLoad();
      // Same once-per-document latch as the admission slot: a raw onLoad
      // would fire for a replaced document and again after `ready`.
      onDisplayReadyRef.current?.();
    }
  }, [onIframeLoad]);
  const isVisible = mountKey !== null && revealedKey === mountKey;

  // --- cohesive-block bridge -----------------------------------------------

  const islandsEditableRef = useRef(islandsEditable);
  islandsEditableRef.current = islandsEditable;
  const isInteractiveRef = useRef(isInteractive);
  isInteractiveRef.current = isInteractive;
  const onBlockMessageRef = useRef(onBlockMessage);
  onBlockMessageRef.current = onBlockMessage;
  const onRuntimeReadyRef = useRef(onRuntimeReady);
  onRuntimeReadyRef.current = onRuntimeReady;
  const hintRef = useRef(false);
  hintRef.current = hovered && islandHint;
  const editFocusPointRef = useRef(editFocusPoint);
  editFocusPointRef.current = editFocusPoint;

  const postRuntimeConfig = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      createBlockConfigMessage({
        editable: islandsEditableRef.current,
        interactive: isInteractiveRef.current,
        hint: hintRef.current,
        focusPoint: editFocusPointRef.current,
        theme: themeRef.current,
      }),
      "*",
    );
  }, []);

  const postToRuntime = useCallback((message: unknown) => {
    iframeRef.current?.contentWindow?.postMessage(message, "*");
  }, []);

  // The block webfonts, handed over the bridge (see blockFonts.ts): the
  // frame's opaque origin has no HTTP cache, so it must not fetch them
  // itself. Sent once per document — on the runtime's request at boot and,
  // as a backstop, on `ready` — to the frame that asked; a document replaced
  // in between never receives the previous one's fonts.
  const fontsSentForKeyRef = useRef<string | null>(null);
  const provisionFonts = useCallback(() => {
    const frame = iframeRef.current;
    const key = mountKeyRef.current;
    if (!frame || !key || fontsSentForKeyRef.current === key) return;
    fontsSentForKeyRef.current = key;
    void loadBlockFonts()
      .then((faces) => {
        if (iframeRef.current !== frame || mountKeyRef.current !== key) return;
        frame.contentWindow?.postMessage(createBlockFontsMessage(faces), "*");
      })
      .catch(() => {
        // The frame renders on the system stacks after its own short wait;
        // the next document may ask again.
        if (fontsSentForKeyRef.current === key) {
          fontsSentForKeyRef.current = null;
        }
      });
  }, []);

  // When a forwarded click last hit something interactive. Rapid clicks on a
  // control (a stepper, a checkbox) must read as repeated activation — the
  // second click is forwarded too, and the double-click gesture is
  // suppressed, so operating a control can never yank the user into a
  // different surface.
  const lastInteractiveClickAtRef = useRef(0);
  const INTERACTIVE_DOUBLE_CLICK_WINDOW_MS = 500;
  const onInteractiveClickThroughRef = useRef(onInteractiveClickThrough);
  onInteractiveClickThroughRef.current = onInteractiveClickThrough;
  // An island click answers with an edit-request AND a click-result for the
  // same gesture, back to back. The edit-request opens an edit session; the
  // click-result must NOT then take the interactive doorway — the host's
  // mode state hasn't re-rendered yet, so a guard on it cannot see the
  // session it would be downgrading. Suppress the doorway for exactly the
  // result that follows an edit-request.
  const editRequestPendingRef = useRef(false);
  // Forwarded clicks answered so far vs. sent: a double-click decision must
  // wait for the pair's results (the answer to "did either click hit a
  // control" lives in the frame), so a gesture that fires before the last
  // result returns is queued and decided when it lands.
  const pendingForwardsRef = useRef(0);
  const queuedDoubleClickRef = useRef<BlockEntryPoint | null>(null);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const frame = iframeRef.current;
      // Authenticate by source, NOT origin: every opaque-origin frame on the
      // page reports its origin as the string "null", so an origin check
      // would accept messages from any other block.
      if (!frame || event.source !== frame.contentWindow) return;
      const msg = parseBlockMessage(event.data);
      if (!msg) return;
      if (msg.type === "fonts-request") {
        provisionFonts();
        return;
      }
      if (msg.type === "ready") {
        provisionFonts();
        reveal();
        postRuntimeConfig();
        onRuntimeReadyRef.current?.({ islands: msg.islands ?? 0 });
        return;
      }
      if (msg.type === "hover-result") {
        // Only answers from the current hover generation count: the host is
        // the authority on whether the pointer is over the block, and an
        // answer that crossed a leave in flight must not reopen the shield.
        if (msg.gen === hoverGenRef.current) setHoverKind(msg.kind);
        return;
      }
      if (msg.type === "context-menu") {
        // Re-raise as a real contextmenu event on the card, at the screen
        // position the content point maps to, so it bubbles into the canvas
        // node's handler exactly like a right-click on the shield.
        const host = hostRef.current;
        if (!host) return;
        const rect = host.getBoundingClientRect();
        const scale = host.offsetWidth > 0 ? rect.width / host.offsetWidth : 1;
        host.dispatchEvent(
          new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            button: 2,
            clientX: rect.left + msg.x * scale,
            clientY: rect.top + msg.y * scale,
          }),
        );
        return;
      }
      if (msg.type === "edit-request") {
        editRequestPendingRef.current = true;
        onBlockMessageRef.current?.(msg);
        return;
      }
      if (msg.type === "click-result") {
        const afterEditRequest = editRequestPendingRef.current;
        editRequestPendingRef.current = false;
        pendingForwardsRef.current = Math.max(
          0,
          pendingForwardsRef.current - 1,
        );
        if (msg.interactive) {
          lastInteractiveClickAtRef.current = Date.now();
          // The doorway: a control took the click, so hand the block the
          // real pointer — everything after this is native events. Not
          // for editable text: that gesture already opened an edit session.
          if (!afterEditRequest) onInteractiveClickThroughRef.current?.();
        } else {
          // A plain click closes the window: the double-click guard asks
          // whether EITHER constituent click of a pair hit a control, and
          // that is a property of the latest forwarded click, not of any
          // control clicked earlier within the same half-second.
          lastInteractiveClickAtRef.current = 0;
        }
        // A text-entry control took the caret in-frame; focus the iframe
        // element so keystrokes route into that document — otherwise typing
        // falls through to canvas hotkeys (Delete removes the node…).
        // Retried after a beat: entering inline interactive re-renders the
        // node and React Flow's selection focus steals the frame's focus
        // back in the same tick.
        if (msg.focus) {
          frame.focus();
          window.setTimeout(() => iframeRef.current?.focus(), 60);
        }
        // A double-click that arrived while this result was in flight can
        // now be decided with the pair fully answered.
        if (pendingForwardsRef.current === 0 && queuedDoubleClickRef.current) {
          const point = queuedDoubleClickRef.current;
          queuedDoubleClickRef.current = null;
          decideDoubleClickRef.current(point);
        }
        return;
      }
      if (msg.type === "link") {
        // Must open synchronously: the click's user activation propagates to
        // this frame but expires almost immediately.
        let parsed: URL;
        try {
          parsed = new URL(msg.href);
        } catch {
          return;
        }
        if (parsed.protocol !== "https:") return;
        window.open(parsed.href, "_blank", "noopener,noreferrer");
        return;
      }
      onBlockMessageRef.current?.(msg);
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [postRuntimeConfig, provisionFonts, reveal]);

  // Mode flips (and hover-hint / caret-target / theme changes) re-send config
  // to the already-mounted runtime (fresh mounts are covered by the ready
  // handshake). A theme change is applied in place there — never a remount.
  useEffect(() => {
    postRuntimeConfig();
  }, [
    islandsEditable,
    isInteractive,
    hovered,
    islandHint,
    editFocusPoint,
    theme,
    postRuntimeConfig,
  ]);

  // New document: visibility follows its own key (see revealedKey), so
  // nothing to hide here.
  useEffect(() => {
    // A replaced document answers none of the previous one's clicks.
    pendingForwardsRef.current = 0;
    queuedDoubleClickRef.current = null;
  }, [mountKey]);

  // Resting hover, forwarded like clicks: the shield holds the pointer, so
  // the frame never sees :hover. One classification per animation frame
  // while the pointer moves over a resting block; the answer drives the
  // shield's cursor so the gesture is announced before it happens.
  const [hoverKind, setHoverKind] = useState<BlockHoverKind>("none");
  const hoverRafRef = useRef(0);
  const hoverPointRef = useRef<BlockEntryPoint | null>(null);
  // Bumped on every leave (and on going live); answers carrying an older
  // generation are dropped.
  const hoverGenRef = useRef(0);
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!clickThrough || iframeActive) return;
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      hoverPointRef.current = {
        x: (event.clientX - rect.left) / scale,
        y: (event.clientY - rect.top) / scale,
      };
      if (hoverRafRef.current) return;
      hoverRafRef.current = window.requestAnimationFrame(() => {
        hoverRafRef.current = 0;
        const point = hoverPointRef.current;
        if (point) {
          postToRuntime(createBlockHoverMessage(point, hoverGenRef.current));
        }
      });
    },
    [clickThrough, iframeActive, postToRuntime],
  );
  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    if (hoverRafRef.current) {
      window.cancelAnimationFrame(hoverRafRef.current);
      hoverRafRef.current = 0;
    }
    hoverPointRef.current = null;
    hoverGenRef.current += 1;
    setHoverKind("none");
    postToRuntime(createBlockHoverLeaveMessage());
  }, [postToRuntime]);
  // Over a control the shield steps aside: the control is native (its own
  // hover state, drags, wheel), and the runtime reports both the click (the
  // doorway) and the pointer leaving it (which closes the hole again).
  // Islands stay behind the shield — their click is an edit request.
  const shieldOpen =
    !iframeActive &&
    clickThrough &&
    (hoverKind === "control" ||
      hoverKind === "pointer" ||
      hoverKind === "link" ||
      hoverKind === "field");
  // A live frame owns its own hover; drop the resting tag when it goes live.
  useEffect(() => {
    if (iframeActive) {
      hoverGenRef.current += 1;
      setHoverKind("none");
      postToRuntime(createBlockHoverLeaveMessage());
    }
  }, [iframeActive, postToRuntime]);

  // Where the pointer went down, to tell a click from a completed drag —
  // React Flow can let a click fire after a node drag, and forwarding that
  // would push a button the user was only moving.
  const clickPointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const handlePointerDownCapture = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      clickPointerDownRef.current = { x: event.clientX, y: event.clientY };
    },
    [],
  );

  const handleRootClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!clickThrough || iframeActive) return;
      // First clicks always forward. A second click (detail 2) forwards ONLY
      // when the first landed on a control — a stepper clicked fast gets both
      // activations — and never when it landed on plain content, where the
      // pair is a double-click gesture instead.
      if (event.detail !== 1) {
        const sinceInteractive = Date.now() - lastInteractiveClickAtRef.current;
        if (
          event.detail !== 2 ||
          sinceInteractive > INTERACTIVE_DOUBLE_CLICK_WINDOW_MS
        ) {
          return;
        }
      }
      // Selection modifiers are canvas gestures (shift-click adds to the
      // selection); never steal them for the content.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const down = clickPointerDownRef.current;
      if (
        down &&
        (Math.abs(event.clientX - down.x) > 4 ||
          Math.abs(event.clientY - down.y) > 4)
      ) {
        return;
      }
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      pendingForwardsRef.current += 1;
      postToRuntime(
        createBlockClickMessage({
          x: (event.clientX - rect.left) / scale,
          y: (event.clientY - rect.top) / scale,
        }),
      );
    },
    [clickThrough, iframeActive, postToRuntime],
  );

  // The double-click decision proper, run once the pair's results are in.
  // Operating a control is never also a gesture: if either constituent click
  // hit something interactive, the pair stays two activations.
  const onDoubleClickRef = useRef(onDoubleClick);
  onDoubleClickRef.current = onDoubleClick;
  const clickThroughRef = useRef(clickThrough);
  clickThroughRef.current = clickThrough;
  const decideDoubleClickRef = useRef((point: BlockEntryPoint) => {
    if (
      clickThroughRef.current &&
      Date.now() - lastInteractiveClickAtRef.current <
        INTERACTIVE_DOUBLE_CLICK_WINDOW_MS
    ) {
      return;
    }
    onDoubleClickRef.current?.(point);
  });

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onDoubleClick) return;
      // Convert to iframe content coordinates: the card may be scaled by the
      // React Flow viewport, so divide the on-screen offset back down by the
      // rendered/layout width ratio.
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      const point = {
        x: (event.clientX - rect.left) / scale,
        y: (event.clientY - rect.top) / scale,
      };
      // Results still in flight for the pair's clicks: decide when they
      // land rather than guessing from a stale timestamp. A runtime that
      // never answers (no script, frame replaced) must not strand the
      // gesture — a short fallback decides with what is known.
      if (clickThrough && pendingForwardsRef.current > 0) {
        queuedDoubleClickRef.current = point;
        window.setTimeout(() => {
          if (queuedDoubleClickRef.current !== point) return;
          queuedDoubleClickRef.current = null;
          pendingForwardsRef.current = 0;
          decideDoubleClickRef.current(point);
        }, 250);
        return;
      }
      decideDoubleClickRef.current(point);
    },
    [onDoubleClick, clickThrough],
  );

  const displayTitle = (path && getFileNameFromPath(path)) || "HTML";

  return (
    <div
      ref={hostRef}
      className={cn(
        "group relative h-full w-full overflow-hidden",
        !frameless && "rounded-xl",
        // At rest a selection ring; inside the block none — the node's
        // resizer frame turns blue and is the single live border.
        selected && !focused && "ring-2 ring-canvas-selection/60",
        className,
      )}
      style={{ colorScheme: theme, ...style }}
      onDoubleClick={handleDoubleClick}
      onClick={handleRootClick}
      onPointerDownCapture={handlePointerDownCapture}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      {/* Placeholder until the live document paints: the server-rendered
          card preview when we have one (share view), else the same loading
          skeleton the node wore on the way in. Frameless blocks skip the
          plate, and transparent blocks skip the preview too — the JPEG is
          opaque, and an opaque plate under a see-through block breaks the
          canvas showing through (worse than briefly showing nothing). */}
      {!isVisible && (
        <HtmlCardPlaceholder
          previewImageUrl={transparent ? null : previewImageUrl}
          showPlate={!frameless}
        />
      )}
      <div
        className={cn(
          // Instant swap from the placeholder: an opacity transition inside
          // the canvas viewport composites it in WebKit and blurs every
          // node for its duration.
          "absolute inset-0",
          isVisible ? "opacity-100" : "opacity-0",
          !transparent && !frameless && "bg-card",
        )}
        style={{ colorScheme: theme }}
      >
        {displayDoc && admitted && (
          <iframe
            key={displayDoc.mountKey}
            ref={iframeRef}
            srcDoc={displayDoc.srcDoc}
            className="h-full w-full border-0"
            style={{
              pointerEvents: iframeActive || shieldOpen ? "auto" : "none",
              colorScheme: theme,
              background: "transparent",
            }}
            sandbox="allow-scripts"
            title={displayTitle}
            onLoad={reveal}
          />
        )}
      </div>
      {/* Host-DOM hit plate: guarantees canvas gestures (wheel pan, drag pan,
          marquee) work over the block whenever the content doesn't own the
          pointer. Double-clicks bubble to the card root. */}
      {!iframeActive && (
        <HtmlBlockShield
          hintLabel={hintLabel}
          cursor={hoverKind === "island" ? "text" : "default"}
          open={shieldOpen}
        />
      )}
    </div>
  );
}
